/**
 * Mapped seats → non-overlapping axis-aligned scan rectangles.
 *
 * Seat pan/tilt MUST use acsSeatMath (ACS clone — DO NOT CHANGE).
 *
 * Tile estimates use ACS zoom calibration (panStep/tiltStep via socket) + axis walk
 * so merges stay under the API hard cap (MAX_CROWD_COUNT_TILES = 200).
 */
import { seatsFromMap } from './seatsFromMap';

const seatId = (s) => `${s.section}|${s.row}|${s.seat}`;

/** Same cap as brizicam-api crowdCount/record.js MAX_CROWD_COUNT_TILES. */
export const MAX_SCAN_TILES = 200;
/** ACS scanHelper MAX_AXIS_STOPS — each axis is hard-capped. */
const MAX_AXIS_STOPS = 64;
const SKIP_SECTIONS = new Set(['brizi']);

const requireZoomSteps = (steps) => {
  const panStep = Number(steps?.panStep);
  const tiltStep = Number(steps?.tiltStep);
  const zoom = Number(steps?.zoom);
  if (!Number.isFinite(panStep) || !Number.isFinite(tiltStep) || !panStep || !tiltStep) {
    throw new Error('Zoom calibration steps (panStep, tiltStep) are required.');
  }
  if (!Number.isFinite(zoom)) {
    throw new Error('Zoom is required.');
  }
  return { panStep, tiltStep, zoom };
};

/** ACS-style axis stop count: start + steps until past end (includes overshoot). */
export const axisStopCount = (span, step) => {
  const absSpan = Math.abs(Number(span));
  const absStep = Math.abs(Number(step));
  if (!Number.isFinite(absSpan) || !Number.isFinite(absStep) || absStep === 0) return 1;
  if (absSpan === 0) return 1;
  return Math.min(MAX_AXIS_STOPS, Math.floor(absSpan / absStep) + 2);
};

export const estimateTileCount = (box, steps) => {
  if (!box) return 0;
  const { panStep, tiltStep } = requireZoomSteps(steps);
  const cols = axisStopCount(box.endPan - box.startPan, panStep);
  const rows = axisStopCount(box.endTilt - box.startTilt, tiltStep);
  return cols * rows;
};

const skipSection = (name) => SKIP_SECTIONS.has(String(name || '').trim().toLowerCase());

const uniqueSeats = (seats) => {
  const seen = new Set();
  return (seats || []).filter((s) => {
    const id = seatId(s);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const mappedSeats = (camMap, rowDict) => uniqueSeats(seatsFromMap(camMap, rowDict))
  .filter((s) => !skipSection(s.section));

const aabbFromSeats = (seats, zoom) => {
  let minPan = Infinity;
  let minTilt = Infinity;
  let maxPan = -Infinity;
  let maxTilt = -Infinity;
  const sections = [];
  seats.forEach((s) => {
    minPan = Math.min(minPan, s.pan);
    minTilt = Math.min(minTilt, s.tilt);
    maxPan = Math.max(maxPan, s.pan);
    maxTilt = Math.max(maxTilt, s.tilt);
    if (s.section && !sections.includes(s.section)) sections.push(s.section);
  });
  if (!Number.isFinite(minPan) || !Number.isFinite(minTilt)) return null;
  const box = {
    startPan: Math.floor(minPan),
    startTilt: Math.floor(minTilt),
    endPan: Math.ceil(maxPan),
    endTilt: Math.ceil(maxTilt),
    zoom,
    sections,
  };
  return box;
};

const panSpan = (b) => Math.max(0, b.endPan - b.startPan);
const tiltSpan = (b) => Math.max(0, b.endTilt - b.startTilt);
const isPoint = (b) => panSpan(b) === 0 && tiltSpan(b) === 0;

const median = (values) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Allowed gap: half the typical region’s longer side. Nearby strips merge; far decks do not. */
const mergePad = (boxes) => {
  const spans = boxes
    .map((b) => Math.max(panSpan(b), tiltSpan(b)))
    .filter((s) => s > 0);
  return spans.length ? median(spans) / 2 : 0;
};

const axisGap = (a0, a1, b0, b1) => Math.max(0, b0 - a1, a0 - b1);

const nearby = (a, b, pad) => (
  axisGap(a.startPan, a.endPan, b.startPan, b.endPan) <= pad
  && axisGap(a.startTilt, a.endTilt, b.startTilt, b.endTilt) <= pad
);

const unionBox = (a, b) => ({
  startPan: Math.min(a.startPan, b.startPan),
  startTilt: Math.min(a.startTilt, b.startTilt),
  endPan: Math.max(a.endPan, b.endPan),
  endTilt: Math.max(a.endTilt, b.endTilt),
  zoom: Math.max(a.zoom, b.zoom),
  sections: [...a.sections, ...b.sections.filter((s) => !a.sections.includes(s))],
});

const canMergeBoxes = (a, b, pad, steps) => {
  if (!nearby(a, b, pad)) return false;
  return estimateTileCount(unionBox(a, b), steps) <= MAX_SCAN_TILES;
};

const mergeNearby = (boxes, steps) => {
  const out = boxes.filter((b) => !isPoint(b));
  const pad = mergePad(out);
  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < out.length; i += 1) {
      for (let j = i + 1; j < out.length; j += 1) {
        if (canMergeBoxes(out[i], out[j], pad, steps)) {
          out[i] = unionBox(out[i], out[j]);
          out.splice(j, 1);
          merged = true;
          break;
        }
      }
      if (merged) break;
    }
  }
  return out;
};

/** Bisect oversized AABBs along the longer axis until each is ≤ MAX_SCAN_TILES. */
const splitUnderTileCap = (box, steps) => {
  if (estimateTileCount(box, steps) <= MAX_SCAN_TILES) return [box];

  const pSpan = panSpan(box);
  const tSpan = tiltSpan(box);
  if (pSpan <= 0 && tSpan <= 0) return [box];

  let a;
  let b;
  if (pSpan >= tSpan) {
    const mid = Math.floor((box.startPan + box.endPan) / 2);
    if (mid <= box.startPan || mid >= box.endPan) return [box];
    a = { ...box, endPan: mid };
    b = { ...box, startPan: mid };
  } else {
    const mid = Math.floor((box.startTilt + box.endTilt) / 2);
    if (mid <= box.startTilt || mid >= box.endTilt) return [box];
    a = { ...box, endTilt: mid };
    b = { ...box, startTilt: mid };
  }

  return [
    ...splitUnderTileCap(a, steps),
    ...splitUnderTileCap(b, steps),
  ];
};

const nameFromSections = (sections) => (
  (sections || []).filter(Boolean).join(' + ') || 'Mapped seats'
);

const uniqueNames = (boxes, steps) => {
  const used = new Map();
  return boxes.map((box) => {
    const base = nameFromSections(box.sections);
    const n = (used.get(base) || 0) + 1;
    used.set(base, n);
    const named = {
      name: n === 1 ? base : `${base} (${n})`,
      startPan: box.startPan,
      // Mosaic row 0 is start. Higher tilt is looking down (lower bowl), so
      // start at max tilt — same order as manual Grid captures (tilt 10 → 0).
      startTilt: box.endTilt,
      endPan: box.endPan,
      endTilt: box.startTilt,
      zoom: box.zoom,
    };
    return {
      ...named,
      tileEstimate: estimateTileCount(named, steps),
    };
  });
};

export const scanBoundsFromSeats = (camMap, rowDict, zoomSteps) => {
  const seats = mappedSeats(camMap, rowDict);
  if (!seats.length) return [];

  const steps = requireZoomSteps(zoomSteps);

  const groups = new Map();
  seats.forEach((s) => {
    const key = s.regionKey || s.section || '?';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });

  const boxes = [];
  groups.forEach((list) => {
    const box = aabbFromSeats(list, steps.zoom);
    if (box) boxes.push(box);
  });

  const merged = mergeNearby(boxes, steps);
  const capped = merged.flatMap((box) => splitUnderTileCap(box, steps));
  return uniqueNames(capped, steps);
};

const finite = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
};

/** Place mapped seats into mosaic [0,1] using scan start + signed steps. */
export const placeSeatsOnMosaic = (seats, {
  startPan, startTilt, panStep, tiltStep, rows, cols,
}) => {
  const aPan = finite(startPan);
  const aTilt = finite(startTilt);
  const dPan = finite(panStep);
  const dTilt = finite(tiltStep);
  const rowCount = Math.max(1, Number(rows) || 0);
  const colCount = Math.max(1, Number(cols) || 0);
  if (aPan == null || aTilt == null || !dPan || !dTilt) return [];

  return (seats || []).flatMap((seat) => {
    if (skipSection(seat.section)) return [];
    const pan = finite(seat.pan);
    const tilt = finite(seat.tilt);
    if (pan == null || tilt == null) return [];
    const colF = (pan - aPan) / dPan;
    const rowF = (tilt - aTilt) / dTilt;
    if (!Number.isFinite(colF) || !Number.isFinite(rowF)) return [];
    const mx = (colF + 0.5) / colCount;
    const my = (rowF + 0.5) / rowCount;
    if (mx < -0.05 || mx > 1.05 || my < -0.05 || my > 1.05) return [];
    return [{
      mx: Math.min(1, Math.max(0, mx)),
      my: Math.min(1, Math.max(0, my)),
      section: seat.section,
      row: seat.row,
      seat: seat.seat,
      regionKey: seat.regionKey,
    }];
  });
};

const tileAt = (byKey, tiles, row, col) => (
  byKey.get(`${row},${col}`) || tiles.find((t) => t.row === row && t.col === col)
);

/** Fill missing plan fields from adjacent tile pan/tilt. */
const stepsFromTiles = (tiles, startPan, startTilt, panStep, tiltStep) => {
  if (!tiles.length) {
    return {
      startPan, startTilt, panStep, tiltStep,
    };
  }
  const byKey = new Map(tiles.map((t) => [`${t.row},${t.col}`, t]));
  const origin = tileAt(byKey, tiles, 0, 0);
  const right = tileAt(byKey, tiles, 0, 1);
  const down = tileAt(byKey, tiles, 1, 0);
  return {
    startPan: startPan ?? (origin ? finite(origin.pan) : null),
    startTilt: startTilt ?? (origin ? finite(origin.tilt) : null),
    panStep: panStep || (origin && right ? finite(right.pan) - finite(origin.pan) : null),
    tiltStep: tiltStep || (origin && down ? finite(down.tilt) - finite(origin.tilt) : null),
  };
};

/** Resolve mosaic geometry from scan report (preferred) or region + tiles. */
export const resolveMosaicPlan = (report, region, grid) => {
  const plan = report?.plan || {};
  const input = report?.input || {};
  const tiles = report?.result?.tiles || region?.tiles || [];
  const rows = finite(plan.rows) || finite(region?.rows) || finite(grid?.rows);
  const cols = finite(plan.cols) || finite(region?.cols) || finite(grid?.cols);
  let startPan = finite(input.startPan) ?? finite(region?.startPan);
  let startTilt = finite(input.startTilt) ?? finite(region?.startTilt);
  let panStep = finite(plan.panStep);
  let tiltStep = finite(plan.tiltStep);

  if ((!panStep || !tiltStep || startPan == null || startTilt == null) && tiles.length) {
    ({
      startPan, startTilt, panStep, tiltStep,
    } = stepsFromTiles(tiles, startPan, startTilt, panStep, tiltStep));
  }

  if (!(rows > 0 && cols > 0)) return null;
  if (startPan == null || startTilt == null || !panStep || !tiltStep) return null;
  return {
    startPan, startTilt, panStep, tiltStep, rows, cols,
  };
};

const SRS_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1',
];

const colorForKey = (key, index) => {
  const s = String(key || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.codePointAt(i)) % 2147483647;
  }
  return SRS_COLORS[Math.abs(hash || index) % SRS_COLORS.length];
};

/** Median nearest-neighbor pitch in mosaic space (for marker size). */
const medianSeatPitch = (placed) => {
  const byRow = new Map();
  placed.forEach((s) => {
    const key = `${s.regionKey || s.section}|${s.row}`;
    const list = byRow.get(key) || [];
    list.push(s);
    byRow.set(key, list);
  });
  const gaps = [];
  byRow.forEach((list) => {
    list.sort((a, b) => Number(a.seat) - Number(b.seat) || a.mx - b.mx);
    for (let i = 1; i < list.length; i += 1) {
      if (Number(list[i].seat) - Number(list[i - 1].seat) === 1) {
        const d = Math.hypot(list[i].mx - list[i - 1].mx, list[i].my - list[i - 1].my);
        if (d > 1e-6) gaps.push(d);
      }
    }
  });
  return gaps.length ? median(gaps) : null;
};

/**
 * Mapped seats as mosaic markers for grid preview overlays.
 * @returns {{ mx, my, half, color, label, regionKey }[]}
 */
export const srsMarkersForOverlay = (camMap, rowDict, report, region, grid) => {
  const mosaic = resolveMosaicPlan(report, region, grid);
  if (!mosaic) return [];
  const placed = placeSeatsOnMosaic(mappedSeats(camMap, rowDict), mosaic);
  if (!placed.length) return [];

  const pitch = medianSeatPitch(placed)
    || (1 / Math.max(mosaic.rows, mosaic.cols)) * 0.35;
  const half = Math.min(0.04, Math.max(0.002, pitch * 0.35));
  const keyIndex = new Map();

  return placed.map((s) => {
    const key = s.regionKey || s.section || '?';
    if (!keyIndex.has(key)) keyIndex.set(key, keyIndex.size);
    return {
      mx: s.mx,
      my: s.my,
      half,
      color: colorForKey(key, keyIndex.get(key)),
      label: `${s.section} ${s.row}-${s.seat}`,
      regionKey: key,
    };
  });
};
